import React, { useCallback, useEffect, useRef, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, Navigate, useLocation, useNavigate } from 'react-router-dom';
import {
  AppBar,
  Avatar,
  Badge,
  Box,
  Button,
  Chip,
  Collapse,
  CssBaseline,
  Divider,
  Drawer,
  InputAdornment,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  Menu,
  MenuItem,
  Popover,
  Stack,
  TextField,
  Toolbar,
  Tooltip,
  Typography,
  ThemeProvider,
  IconButton,
  useMediaQuery,
} from '@mui/material';
import {
  Insights as InsightsIcon,
  Description as ConsentsIcon,
  Map as MapIcon,
  Settings as SettingsIcon,
  MenuBook as GuideIcon,
  AccountTree as AuthorityIcon,
  Search as SearchIcon,
  NotificationsNone as NotificationsIcon,
  KeyboardArrowDown as ArrowDownIcon,
  Shield as ShieldIcon,
  Storage as DataOperationsIcon,
  FactCheck as AuditIcon,
  Group as TeamAccessIcon,
  Menu as MenuIcon,
  Category as ConsentTypesIcon,
  ExpandMore as ExpandMoreIcon,
  CloudUpload as DataImportIcon,
  HelpOutline as HelpIcon,
  AutoAwesome as CopilotIcon,
} from '@mui/icons-material';

import theme, { sidebar, workspace } from './theme';

// Pages
import Landing from './pages/Landing';
import Dashboard from './pages/Dashboard';
import Consents from './pages/Consents';
import MapView from './pages/MapView';
import Settings from './pages/Settings';
import EditConsent from './pages/EditConsent';
import ConsentDetail from './pages/ConsentDetail';
import ConsentGuide from './pages/ConsentGuide';
import Authorities from './pages/Authorities';
import DataOperations from './pages/DataOperations';
import IngestionRunDetail from './pages/IngestionRunDetail';
import Notifications from './pages/Notifications';
import AuditLog from './pages/AuditLog';
import Login from './pages/Login';
import TeamAccess from './pages/TeamAccess';
import AcceptInvitation from './pages/AcceptInvitation';
import ConsentTypes from './pages/ConsentTypes';
import ConsentImport from './pages/ConsentImport';
import BackupAssignments from './pages/BackupAssignments';
import HelpGuidance from './pages/HelpGuidance';
import { ProductTourProvider, useTourIntent } from './tour/TourProvider';
import { CopilotProvider, useCopilot } from './copilot/CopilotProvider';
import { getActiveProjectId, ProjectContext, setActiveProject } from './services/projectContext';

const drawerWidth = 248;
const API_BASE = process.env.REACT_APP_API_URL || '/api';

const AuthGate: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, setState] = useState<'loading' | 'authenticated' | 'signed_out'>('loading');
  useEffect(() => {
    fetch(`${API_BASE}/auth/session`).then(response => {
      setState(response.ok ? 'authenticated' : 'signed_out');
    }).catch(() => setState('signed_out'));
  }, []);
  if (state === 'loading') return <Box sx={{ minHeight: '100vh', bgcolor: workspace.background }} />;
  if (state === 'signed_out') return <Login />;
  return <>{children}</>;
};

// Team & Access contains customer project-administration tools, so it must be
// guarded at the route as well as hidden from the sidebar. Direct URLs must
// not bypass the permission check.
const TeamAccessGate: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, setState] = useState<'loading' | 'allowed' | 'denied'>('loading');
  useEffect(() => {
    fetch(`${API_BASE}/auth/session`).then(response => response.ok ? response.json() : Promise.reject()).then(data => {
      const user = data.user;
      const allowed = Boolean(user?.is_platform_admin || user?.projects?.some((project: any) => (
        project.role === 'project_administrator' ||
        project.permissions?.includes('*') ||
        project.permissions?.includes('users.manage') ||
        project.permissions?.includes('roles.manage')
      )));
      setState(allowed ? 'allowed' : 'denied');
    }).catch(() => setState('denied'));
  }, []);
  if (state === 'loading') return <Box sx={{ minHeight: '100vh', bgcolor: workspace.background }} />;
  if (state === 'denied') return <Navigate to="/dashboard" replace />;
  return <>{children}</>;
};

type Surface = 'team' | 'templates' | 'import' | 'source' | 'settings';
const SurfaceGate: React.FC<{ surface: Surface; children: React.ReactNode }> = ({ surface, children }) => {
  const [state, setState] = useState<'loading' | 'allowed' | 'denied'>('loading');
  useEffect(() => {
    fetch(`${API_BASE}/auth/session`).then(response => response.ok ? response.json() : Promise.reject()).then(data => {
      const user = data.user;
      const projects = user?.projects || [];
      const allowed = Boolean(user?.is_platform_admin || projects.some((project: any) => {
        const permissions = project.permissions || [];
        if (surface === 'team') return project.role === 'project_administrator' || permissions.includes('*') || permissions.includes('users.manage') || permissions.includes('roles.manage');
        if (surface === 'templates') return project.role === 'project_administrator' || permissions.includes('*') || permissions.includes('templates.manage');
        if (surface === 'import') return permissions.includes('*') || permissions.includes('imports.run') || permissions.includes('source.run');
        if (surface === 'source') return permissions.includes('*') || permissions.includes('source.view');
        return project.role === 'project_administrator' || permissions.includes('*') || permissions.includes('settings.manage');
      }));
      setState(allowed ? 'allowed' : 'denied');
    }).catch(() => setState('denied'));
  }, [surface]);
  if (state === 'loading') return <Box sx={{ minHeight: '100vh', bgcolor: workspace.background }} />;
  if (state === 'denied') return <Navigate to="/dashboard" replace />;
  return <>{children}</>;
};

// Navigation, grouped by workflow: daily project work, regulatory reference, activity trail, then configuration
const navGroups: { id: string; header: string; items: { text: string; icon: React.ReactNode; path: string }[] }[] = [
  {
    id: 'project',
    header: 'Project',
    items: [
      { text: 'Overview', icon: <InsightsIcon fontSize="small" />, path: '/dashboard' },
      { text: 'Consents', icon: <ConsentsIcon fontSize="small" />, path: '/consents' },
      { text: 'Map', icon: <MapIcon fontSize="small" />, path: '/map' },
    ],
  },
  {
    id: 'intelligence',
    header: 'Regulatory Intelligence',
    items: [
      { text: 'Authorities', icon: <AuthorityIcon fontSize="small" />, path: '/authorities' },
      { text: 'Data Sources', icon: <DataOperationsIcon fontSize="small" />, path: '/data-operations' },
      { text: 'Consent Guide', icon: <GuideIcon fontSize="small" />, path: '/guide' },
    ],
  },
  {
    id: 'activity',
    header: 'Activity',
    items: [
      { text: 'Notifications', icon: <NotificationsIcon fontSize="small" />, path: '/notifications' },
      { text: 'Audit Log', icon: <AuditIcon fontSize="small" />, path: '/audit-log' },
    ],
  },
  {
    id: 'help',
    header: 'Help & Guidance',
    items: [
      { text: 'Help & Guidance', icon: <HelpIcon fontSize="small" />, path: '/help' },
    ],
  },
  {
    id: 'administration',
    header: 'Administration',
    items: [
      { text: 'Team & Access', icon: <TeamAccessIcon fontSize="small" />, path: '/team-access' },
      { text: 'Consent Types', icon: <ConsentTypesIcon fontSize="small" />, path: '/consent-types' },
      { text: 'Data Import', icon: <DataImportIcon fontSize="small" />, path: '/data-import/consents' },
      { text: 'Ownership coverage', icon: <TeamAccessIcon fontSize="small" />, path: '/backup-assignments' },
      { text: 'Settings', icon: <SettingsIcon fontSize="small" />, path: '/settings' },
    ],
  },
];

const NAV_COLLAPSED_KEY = 'assertion.nav.collapsedGroups';
const readCollapsedGroups = (): string[] => {
  try {
    const stored = JSON.parse(window.localStorage.getItem(NAV_COLLAPSED_KEY) || '[]');
    return Array.isArray(stored) ? stored : [];
  } catch {
    return [];
  }
};
const isNavItemActive = (pathname: string, path: string) => pathname === path || pathname.startsWith(path + '/');

// Sidebar
const Sidebar: React.FC<{ mobileOpen: boolean; onClose: () => void }> = ({ mobileOpen, onClose }) => {
  const location = useLocation();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [canManageTeam, setCanManageTeam] = useState(false);
  const [canManageTemplates, setCanManageTemplates] = useState(false);
  const [canManageImport, setCanManageImport] = useState(false);
  const [canViewSources, setCanViewSources] = useState(false);
  const [canManageSettings, setCanManageSettings] = useState(false);
  useEffect(() => {
    fetch(`${API_BASE}/auth/session`).then(r => r.ok ? r.json() : null).then(data => {
      const user = data?.user;
      setCanManageTeam(Boolean(user?.is_platform_admin || user?.projects?.some((p: any) => p.role === 'project_administrator' || p.permissions?.includes('*') || p.permissions?.includes('users.manage') || p.permissions?.includes('roles.manage'))));
      setCanManageTemplates(Boolean(user?.is_platform_admin || user?.projects?.some((p: any) => p.role === 'project_administrator' || p.permissions?.includes('*') || p.permissions?.includes('templates.manage'))));
      setCanManageImport(Boolean(user?.is_platform_admin || user?.projects?.some((p: any) => p.permissions?.includes('*') || p.permissions?.includes('imports.run') || p.permissions?.includes('source.run'))));
      setCanViewSources(Boolean(user?.is_platform_admin || user?.projects?.some((p: any) => p.permissions?.includes('*') || p.permissions?.includes('source.view'))));
      setCanManageSettings(Boolean(user?.is_platform_admin || user?.projects?.some((p: any) => p.role === 'project_administrator' || p.permissions?.includes('*') || p.permissions?.includes('settings.manage'))));
    }).catch(() => undefined);
  }, []);

  const [collapsedGroups, setCollapsedGroups] = useState<string[]>(readCollapsedGroups);
  const activeGroupId = navGroups.find(group => group.items.some(item => isNavItemActive(location.pathname, item.path)))?.id;
  // The group owning the current route is force-expanded so the active item is never hidden.
  useEffect(() => { if (activeGroupId) setCollapsedGroups(previous => previous.includes(activeGroupId) ? previous.filter(id => id !== activeGroupId) : previous); }, [activeGroupId]);
  const toggleGroup = (id: string) => setCollapsedGroups(previous => {
    const next = previous.includes(id) ? previous.filter(entry => entry !== id) : [...previous, id];
    try { window.localStorage.setItem(NAV_COLLAPSED_KEY, JSON.stringify(next)); } catch { /* storage unavailable */ }
    return next;
  });

  const content = (
    <>
      {/* Wordmark — clicks through to Portfolio Overview */}
      <Box sx={{ px: 2.5, py: 2.5, cursor: 'pointer' }} component={Link} to="/dashboard" onClick={onClose} style={{ textDecoration: 'none' }}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <ShieldIcon sx={{ color: sidebar.accent, fontSize: 22 }} />
          <Typography sx={{ color: sidebar.textActive, fontWeight: 700, letterSpacing: '0.02em', fontSize: '1.05rem' }}>ASSERTION</Typography>
        </Stack>
        <Typography sx={{ color: sidebar.textMuted, fontSize: '0.7rem', mt: 0.25, letterSpacing: '0.03em' }}>Regulatory Intelligence Platform</Typography>
      </Box>
      <Divider sx={{ borderColor: sidebar.border }} />
      <Box sx={{ overflow: 'auto', flex: 1, py: 1.5 }}>
        {navGroups.map((group, index) => {
          const items = group.items.filter(item => (item.path !== '/team-access' || canManageTeam) && (item.path !== '/backup-assignments' || canManageTeam) && (item.path !== '/consent-types' || canManageTemplates) && (item.path !== '/data-import/consents' || canManageImport) && (item.path !== '/data-operations' || canViewSources) && (item.path !== '/settings' || canManageSettings));
          if (!items.length) return null;
          const open = !collapsedGroups.includes(group.id);
          return (
            <List
              key={group.id}
              dense
              disablePadding
              sx={{ mt: index === 0 ? 0 : 1.5 }}
              subheader={
                <ListSubheader
                  component="div"
                  disableSticky
                  role="button"
                  tabIndex={0}
                  aria-expanded={open}
                  aria-controls={'nav-group-' + group.id}
                  onClick={() => toggleGroup(group.id)}
                  onKeyDown={(event) => { if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); toggleGroup(group.id); } }}
                  sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', bgcolor: 'transparent', color: sidebar.textMuted, fontSize: '0.65rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', lineHeight: '2rem', px: 2.5, cursor: 'pointer', userSelect: 'none', '&:hover': { color: sidebar.text }, '&:focus-visible': { outline: '1px solid ' + sidebar.accent, outlineOffset: -1 } }}
                >
                  {group.header}
                  <ExpandMoreIcon sx={{ fontSize: 16, transform: open ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 220ms cubic-bezier(0.4, 0, 0.2, 1)', '@media (prefers-reduced-motion: reduce)': { transition: 'none' } }} />
                </ListSubheader>
              }
            >
              <Collapse in={open} timeout="auto" id={'nav-group-' + group.id}>
                {items.map((item) => {
                  const selected = isNavItemActive(location.pathname, item.path);
                  return (
                    <ListItemButton key={item.path} component={Link} to={item.path} selected={selected} onClick={onClose} sx={{ mx: 1, my: 0.25, px: 1.5, py: 0.65, borderRadius: 1, color: selected ? sidebar.textActive : sidebar.text, borderLeft: '2px solid', borderLeftColor: selected ? sidebar.accent : 'transparent', '&.Mui-selected': { bgcolor: sidebar.bgHover }, '&.Mui-selected:hover': { bgcolor: sidebar.bgHover }, '&:hover': { bgcolor: sidebar.bgHover } }}>
                      <ListItemIcon sx={{ minWidth: 32, color: selected ? sidebar.accent : 'inherit' }}>{item.icon}</ListItemIcon>
                      <ListItemText primary={item.text} primaryTypographyProps={{ fontSize: '0.85rem', fontWeight: selected ? 600 : 500 }} />
                    </ListItemButton>
                  );
                })}
              </Collapse>
            </List>
          );
        })}
      </Box>
      <Box sx={{ px: 2.5, py: 2, borderTop: `1px solid ${sidebar.border}` }}><Typography sx={{ color: sidebar.textMuted, fontSize: '0.65rem' }}>v1.1.0 · A Linckia product</Typography></Box>
    </>
  );

  return (
    <Drawer
      variant={isMobile ? 'temporary' : 'permanent'}
      open={isMobile ? mobileOpen : true}
      onClose={onClose}
      ModalProps={{ keepMounted: true }}
      PaperProps={{ 'data-tour-id': 'main-navigation' } as any}
      sx={{
        width: drawerWidth,
        flexShrink: 0,
        display: { xs: 'block', md: 'block' },
        '& .MuiDrawer-paper': {
          width: drawerWidth,
          boxSizing: 'border-box',
          top: { xs: 0, md: 64 },
          height: { xs: '100%', md: 'calc(100% - 64px)' },
          bgcolor: sidebar.bg,
          borderRight: `1px solid ${sidebar.border}`,
        },
      }}
    >
      {content}
    </Drawer>
  );
};

// Top bar: global search, project context, notifications, user
const TopBar: React.FC<{ onMenuOpen: () => void }> = ({ onMenuOpen }) => {
  const navigate = useNavigate();
  const [searchValue, setSearchValue] = useState('');
  const [projects, setProjects] = useState<ProjectContext[]>([]);
  const [project, setProject] = useState<ProjectContext | null>(null);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [projectAnchor, setProjectAnchor] = useState<null | HTMLElement>(null);
  const [notificationAnchor, setNotificationAnchor] = useState<null | HTMLElement>(null);
  const notificationButtonRef = useRef<HTMLButtonElement | null>(null);
  const { openCopilot } = useCopilot();
  const [unreadCount, setUnreadCount] = useState(0);
  const [notifications, setNotifications] = useState<any[]>([]);
  const [user, setUser] = useState({ name: 'Sam Taylor', email: 'demo-user', role: 'Consent Manager', team: 'Consents Team', demo: true, is_platform_admin: false });

  useEffect(() => {
    fetch(`${API_BASE}/auth/session`)
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        const list: ProjectContext[] = data?.user?.projects || [];
        setProjects(list);
        const stored = getActiveProjectId();
        const selected = list.find(item => item.id === stored) || list[0] || null;
        if (selected) {
          setProject(selected);
          setActiveProject(selected);
          // Establish a deterministic project scope before page queries run.
          if (stored !== selected.id) window.location.reload();
        }
      })
      .catch(() => undefined);
  }, []);

  const selectProject = (selected: ProjectContext) => {
    setProject(selected);
    setActiveProject(selected);
    window.location.reload();
  };

  const refreshNotifications = async () => {
    const [countResponse, listResponse] = await Promise.all([
      fetch(`${API_BASE}/notifications/unread-count${project ? `?project_id=${project.id}` : ''}`),
      fetch(`${API_BASE}/notifications?limit=5${project ? `&project_id=${project.id}` : ''}`),
    ]);
    if (countResponse.ok) setUnreadCount((await countResponse.json()).count || 0);
    if (listResponse.ok) setNotifications((await listResponse.json()).notifications || []);
  };

  useEffect(() => {
    fetch(`${API_BASE}/auth/session`).then(response => response.ok ? response.json() : null).then(data => {
      if (data?.user) setUser((previous) => ({ ...previous, ...data.user, demo: false, team: data.user.team || previous.team }));
    }).catch(() => undefined);
    refreshNotifications().catch(() => undefined);
    const timer = window.setInterval(() => refreshNotifications().catch(() => undefined), 60000);
    return () => window.clearInterval(timer);
  }, [project]);

  const openNotification = async (item: any) => {
    if (!item.read_at) { await fetch(`${API_BASE}/notifications/${item.id}/read`, { method: 'PATCH' }); }
    setNotificationAnchor(null); await refreshNotifications(); if (item.action_route) navigate(item.action_route);
  };

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchValue.trim()) {
      navigate(`/consents?q=${encodeURIComponent(searchValue.trim())}`);
    }
  };

  const openNotificationsPanel = useCallback(() => {
    if (notificationButtonRef.current) setNotificationAnchor(notificationButtonRef.current);
    refreshNotifications();
  }, []);
  useTourIntent('openNotifications', openNotificationsPanel);
  useTourIntent('closeNotifications', () => setNotificationAnchor(null));

  return (
    <AppBar position="fixed" color="inherit" sx={{ bgcolor: 'background.paper', borderRadius: 0, borderBottom: '1px solid', borderColor: 'divider', boxShadow: '0 1px 4px rgba(15,23,42,0.06)', zIndex: (t) => t.zIndex.drawer + 1 }}>
      <Toolbar sx={{ gap: 2, minHeight: '64px !important', px: { xs: 2, md: 3 } }}>
        <IconButton data-tour-id="nav-trigger" onClick={onMenuOpen} aria-label="Open navigation" sx={{ display: { xs: 'inline-flex', md: 'none' } }}><MenuIcon /></IconButton>
        {project && (projects.length > 1 || user.is_platform_admin ? (
          <>
            <Box data-tour-id="project-selector">
            <Chip
              size="small"
              onClick={(event) => setProjectAnchor(event.currentTarget)}
              label={<><Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>{project.name} ({project.code})</Box><Box component="span" sx={{ display: { xs: 'inline', sm: 'none' } }}>{project.code}</Box></>}
              deleteIcon={<ArrowDownIcon />}
              onDelete={(event) => setProjectAnchor(event.currentTarget as unknown as HTMLElement)}
              sx={{ bgcolor: '#EEF2FF', color: '#2450C6', fontWeight: 600, maxWidth: { xs: 90, sm: 310 } }}
            />
            </Box>
            <Menu anchorEl={projectAnchor} open={!!projectAnchor} onClose={() => setProjectAnchor(null)}>
              {projects.map(item => <MenuItem key={item.id} selected={item.id === project.id} onClick={() => { setProjectAnchor(null); selectProject(item); }}>{item.name} ({item.code})</MenuItem>)}
              {user.is_platform_admin && <><Divider /><MenuItem onClick={() => { setProjectAnchor(null); navigate('/settings?section=projects'); }}>Create Project</MenuItem></>}
            </Menu>
          </>
        ) : <Box data-tour-id="project-selector"><Chip size="small" label={<><Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>{project.name} ({project.code})</Box><Box component="span" sx={{ display: { xs: 'inline', sm: 'none' } }}>{project.code}</Box></>} sx={{ bgcolor: '#EEF2FF', color: '#2450C6', fontWeight: 600 }} /></Box>)}

        <Box component="form" onSubmit={handleSearchSubmit} sx={{ flex: 1, maxWidth: 480, display: { xs: 'none', sm: 'block' } }}>
          <TextField
            size="small"
            fullWidth
            placeholder="Search consents, authorities, references..."
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                </InputAdornment>
              ),
              sx: { bgcolor: '#F4F5F7', borderRadius: 1.5 },
            }}
          />
        </Box>
        <IconButton aria-label="Search consents" onClick={() => navigate('/consents')} sx={{ display: { xs: 'inline-flex', sm: 'none' } }}><SearchIcon /></IconButton>

        <Box sx={{ flex: 1 }} />

        <Tooltip title="Ask ASSERTION Copilot">
          <IconButton data-tour-id="copilot-launcher" onClick={() => openCopilot()} aria-label="Open ASSERTION Copilot">
            <CopilotIcon />
          </IconButton>
        </Tooltip>

        <Tooltip title="Notifications">
          <IconButton ref={notificationButtonRef} data-tour-id="notifications-trigger" onClick={async (event) => { setNotificationAnchor(event.currentTarget); await refreshNotifications(); }} aria-label="Open notifications">
            <Badge badgeContent={unreadCount > 99 ? '99+' : unreadCount > 9 ? '9+' : unreadCount} color="error" invisible={unreadCount === 0}>
              <NotificationsIcon />
            </Badge>
          </IconButton>
        </Tooltip>

        <Popover open={!!notificationAnchor} anchorEl={notificationAnchor} onClose={() => setNotificationAnchor(null)} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }} transformOrigin={{ vertical: 'top', horizontal: 'right' }}>
          <Box data-tour-id="notifications-panel" sx={{ width: 360, maxWidth: 'calc(100vw - 32px)', p: 2 }}><Stack direction="row" justifyContent="space-between" alignItems="center"><Typography variant="h6">Notifications</Typography><Button size="small" onClick={() => { setNotificationAnchor(null); navigate('/notifications'); }}>View all</Button></Stack><Divider sx={{ my: 1 }} />{!notifications.length ? <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>You’re up to date. New consent, deadline and source events will appear here.</Typography> : <Stack divider={<Divider />}><List dense disablePadding>{notifications.map(item => <ListItemButton key={item.id} onClick={() => openNotification(item)} sx={{ px: 0, bgcolor: item.read_at ? 'transparent' : '#F8FAFF' }}><ListItemText primary={item.title} secondary={<>{item.message} · {new Date(item.created_at).toLocaleString()}</>} /></ListItemButton>)}</List></Stack>}{!!notifications.some(item => !item.read_at) && <Button fullWidth size="small" onClick={async () => { await fetch(`${API_BASE}/notifications/read-all`, { method: 'POST' }); await refreshNotifications(); }}>Mark all as read</Button>}</Box>
        </Popover>

        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          onClick={(e) => setAnchorEl(e.currentTarget)}
          sx={{ cursor: 'pointer', pl: 1 }}
        >
          <Avatar sx={{ width: 32, height: 32, bgcolor: '#2450C6', fontSize: '0.85rem' }}>{user.name.split(' ').map(part => part[0]).join('').slice(0, 2).toUpperCase()}</Avatar>
          <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
            <Typography variant="body2" fontWeight={600} lineHeight={1.2}>{user.name}</Typography>
            <Typography variant="caption" color="text.secondary">{user.role}</Typography>
          </Box>
          <ArrowDownIcon fontSize="small" sx={{ color: 'text.secondary' }} />
        </Stack>
        <Menu anchorEl={anchorEl} open={!!anchorEl} onClose={() => setAnchorEl(null)}>
          <MenuItem disabled>{user.name} · {user.team}</MenuItem>
          <Divider />
          <MenuItem disabled>{user.demo ? 'Demo identity (no authentication provider connected)' : user.email}</MenuItem>
          <MenuItem onClick={() => { setAnchorEl(null); navigate('/settings'); }}>Settings</MenuItem>
          <MenuItem onClick={async () => { setAnchorEl(null); await fetch(`${API_BASE}/auth/logout`, { method: 'POST' }); navigate('/login'); }}>Sign out</MenuItem>
        </Menu>
      </Toolbar>
    </AppBar>
  );
};

// Layout component for pages with sidebar + top bar
const AppLayout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  useTourIntent('openMobileNav', () => { if (window.innerWidth < 900) setMobileNavOpen(true); });
  useTourIntent('closeMobileNav', () => setMobileNavOpen(false));
  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: workspace.background }}>
      <TopBar onMenuOpen={() => setMobileNavOpen(true)} />
      <Sidebar mobileOpen={mobileNavOpen} onClose={() => setMobileNavOpen(false)} />
      <Box component="main" sx={{ flexGrow: 1, minWidth: 0, bgcolor: workspace.background, minHeight: '100vh' }}>
        <Toolbar sx={{ minHeight: '64px !important' }} />
        <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 }, minHeight: 'calc(100vh - 64px)', width: '100%', overflowX: 'hidden' }}>{children}</Box>
      </Box>
    </Box>
  );
};

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Router>
        <ProductTourProvider>
        <CopilotProvider>
        <Routes>
          {/* Landing page - no sidebar */}
          <Route path="/" element={<Landing />} />
          <Route path="/login" element={<Login />} />
          <Route path="/accept-invitation" element={<AcceptInvitation />} />
          
          {/* App pages with sidebar */}
          <Route path="/dashboard" element={
            <AuthGate><AppLayout>
              <Dashboard />
            </AppLayout></AuthGate>
          } />
          <Route path="/consents" element={
            <AuthGate><AppLayout>
              <Consents />
            </AppLayout></AuthGate>
          } />
          <Route path="/consents/new" element={
            <AuthGate><AppLayout>
              <EditConsent />
            </AppLayout></AuthGate>
          } />
          <Route path="/consents/:id" element={
            <AuthGate><AppLayout>
              <ConsentDetail />
            </AppLayout></AuthGate>
          } />
          <Route path="/consents/:id/edit" element={
            <AuthGate><AppLayout>
              <EditConsent />
            </AppLayout></AuthGate>
          } />
          <Route path="/map" element={
            <AuthGate><AppLayout>
              <MapView />
            </AppLayout></AuthGate>
          } />
          <Route path="/authorities" element={
            <AuthGate><AppLayout>
              <Authorities />
            </AppLayout></AuthGate>
          } />
          <Route path="/guide" element={
            <AuthGate><AppLayout>
              <ConsentGuide />
            </AppLayout></AuthGate>
          } />
          <Route path="/settings" element={
            <AuthGate><SurfaceGate surface="settings"><AppLayout>
              <Settings />
            </AppLayout></SurfaceGate></AuthGate>
          } />
          <Route path="/data-operations" element={
            <AuthGate><SurfaceGate surface="source"><AppLayout><DataOperations /></AppLayout></SurfaceGate></AuthGate>
          } />
          <Route path="/data-operations/runs/:id" element={
            <AuthGate><SurfaceGate surface="source"><AppLayout><IngestionRunDetail /></AppLayout></SurfaceGate></AuthGate>
          } />
          <Route path="/notifications" element={<AuthGate><AppLayout><Notifications /></AppLayout></AuthGate>} />
          <Route path="/audit-log" element={<AuthGate><AppLayout><AuditLog /></AppLayout></AuthGate>} />
          <Route path="/help" element={<AuthGate><AppLayout><HelpGuidance /></AppLayout></AuthGate>} />
          <Route path="/team-access" element={<AuthGate><TeamAccessGate><AppLayout><TeamAccess /></AppLayout></TeamAccessGate></AuthGate>} />
          <Route path="/consent-types" element={<AuthGate><SurfaceGate surface="templates"><AppLayout><ConsentTypes /></AppLayout></SurfaceGate></AuthGate>} />
          <Route path="/data-import/consents" element={<AuthGate><SurfaceGate surface="import"><AppLayout><ConsentImport /></AppLayout></SurfaceGate></AuthGate>} />
          <Route path="/backup-assignments" element={<AuthGate><SurfaceGate surface="team"><AppLayout><BackupAssignments /></AppLayout></SurfaceGate></AuthGate>} />
        </Routes>
        </CopilotProvider>
        </ProductTourProvider>
      </Router>
    </ThemeProvider>
  );
}

export default App;
